import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import schedulingApi from '../../api/scheduling';
import AvailabilityGrid from './AvailabilityGrid';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function formatDeadline(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// ── Status banner for non-open states ────────────────────────────────────────

function NoPeriodBanner({ message }) {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">Submit Availability</h1>
      <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
        <p className="text-gray-500">{message}</p>
      </div>
    </div>
  );
}

// ── Period header ─────────────────────────────────────────────────────────────

function PeriodHeader({ period, submission }) {
  const deadline = period.availability_deadline
    ? formatDeadline(period.availability_deadline)
    : 'No deadline set';

  const isComplete = submission?.is_complete;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap gap-4 items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-gray-700">
          {formatDate(period.start_date)} — {formatDate(period.end_date)}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">Deadline: {deadline}</p>
      </div>

      <div className="flex items-center gap-3">
        {isComplete ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            Submitted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-700">
            <span className="h-2 w-2 rounded-full bg-yellow-400" />
            Incomplete
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AvailabilityPage() {
  const queryClient = useQueryClient();

  // Fetch the open period
  const { data: period, isLoading: periodLoading, error: periodError } =
    useQuery({
      queryKey: ['period-open'],
      queryFn:  schedulingApi.getPeriods.bind(null),
      select:   (data) => {
        // getPeriods returns a list; find the first OPEN one
        const open = Array.isArray(data) ? data.find((p) => p.status === 'OPEN') : null;
        return open || null;
      },
    });

  // Fetch existing responses once we have a period
  const { data: existingResponses = [] } = useQuery({
    queryKey: ['my-responses', period?.id],
    queryFn:  () => schedulingApi.myResponses(period.id),
    enabled:  !!period,
  });

  // Fetch submission status
  const { data: submission } = useQuery({
    queryKey: ['my-submission', period?.id],
    queryFn:  async () => {
      const list = await schedulingApi.submissionStatus(period.id);
      // submissionStatus is SSL-only; fallback gracefully for SLs
      return Array.isArray(list) ? list[0] : null;
    },
    enabled: !!period,
    retry: false,
  });

  // Local response state: { 'YYYY-MM-DD': 'YES' | 'NO' | 'MAYBE' }
  const [responses, setResponses] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Seed local state from existing responses when they load
  useEffect(() => {
    if (!existingResponses.length) return;
    const seeded = {};
    for (const r of existingResponses) {
      seeded[r.date] = r.response;
    }
    setResponses(seeded);
  }, [existingResponses]);

  // Heavy days for this period
  const heavyDates = period?.heavy_days?.map((h) => h.date) ?? [];

  // Mutation for submitting
  const submitMutation = useMutation({
    mutationFn: (payload) => schedulingApi.submitAvailability(payload),
    onSuccess: (data) => {
      setSubmitError('');
      setSubmitSuccess(
        data.is_complete
          ? 'Availability submitted successfully!'
          : `Saved — ${data.days_total - data.days_answered} day(s) still need a response.`
      );
      queryClient.invalidateQueries({ queryKey: ['my-responses', period?.id] });
      queryClient.invalidateQueries({ queryKey: ['my-submission', period?.id] });
    },
    onError: (err) => {
      setSubmitError(err.message || 'Submission failed. Please try again.');
      setSubmitSuccess('');
    },
  });

  const handleChange = (date, response) => {
    setResponses((prev) => {
      const next = { ...prev };
      if (response === null) delete next[date];
      else next[date] = response;
      return next;
    });
    setSubmitSuccess('');
    setSubmitError('');
  };

  const handleSubmit = () => {
    if (!period) return;
    const responseList = Object.entries(responses).map(([date, response]) => ({
      date,
      response,
    }));
    submitMutation.mutate({ period: period.id, responses: responseList });
  };

  // ── Render states ──────────────────────────────────────────────────────────

  if (periodLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        Loading…
      </div>
    );
  }

  if (periodError || !period) {
    return <NoPeriodBanner message="There is no open availability period right now. Check back later." />;
  }

  const allAnswered = period && Object.keys(responses).length > 0 &&
    (() => {
      const start = new Date(period.start_date + 'T00:00:00');
      const end   = new Date(period.end_date   + 'T00:00:00');
      let count = 0;
      const cur = new Date(start);
      while (cur <= end) { count++; cur.setDate(cur.getDate() + 1); }
      return Object.keys(responses).length >= count;
    })();

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-gray-900">Submit Availability</h1>

      <PeriodHeader period={period} submission={submission} />

      <p className="text-sm text-gray-500">
        Mark each day <strong className="text-green-600">Yes</strong> (available),{' '}
        <strong className="text-red-600">No</strong> (unavailable), or{' '}
        <strong className="text-yellow-600">Maybe</strong> (available but cannot answer the phone before 7 PM).
        The first SL listed on duty must have answered <strong>Yes</strong>.
      </p>

      {/* Feedback banners */}
      {submitError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {submitError}
        </div>
      )}
      {submitSuccess && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {submitSuccess}
        </div>
      )}

      {/* Calendar grid */}
      <AvailabilityGrid
        period={period}
        heavyDates={heavyDates}
        responses={responses}
        onChange={handleChange}
      />

      {/* Submit */}
      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-gray-400">
          {allAnswered
            ? 'All days answered — ready to submit.'
            : 'Answer every day in the period before submitting.'}
        </p>
        <button
          onClick={handleSubmit}
          disabled={submitMutation.isPending || !Object.keys(responses).length}
          className="rounded-lg bg-indigo-600 px-6 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
        >
          {submitMutation.isPending ? 'Saving…' : 'Save Availability'}
        </button>
      </div>
    </div>
  );
}
